import { useState, useCallback, useRef, useEffect } from 'react';
import styles from './TalentSchedulePage.module.css';
import { C, t } from '../Dashboard/components/dashboardConstants';
import CalendarGrid from './components/CalendarGrid';
import UpcomingPanel from './components/UpcomingPanel';
import AddEventModal from './components/AddEventModal';
import EventDetailModal from './components/EventDetailModal';
import DayEventsModal from './components/DayEventsModal';
import { getSchedules, createSchedule, updateSchedule, deleteSchedule } from '../../../api';
import { normalizeScheduleColor } from '../../../shared/schedule/scheduleColors';

function toDateStr(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function monthRange(year, month) {
  const monthStart = new Date(year, month, 1);
  const monthEnd = new Date(year, month + 1, 0);
  const start = new Date(monthStart);
  start.setDate(monthStart.getDate() - monthStart.getDay());
  const end = new Date(monthEnd);
  end.setDate(monthEnd.getDate() + (6 - monthEnd.getDay()));
  return { startDate: toDateStr(start), endDate: toDateStr(end) };
}

function mapSchedule(s, colorOverrides = {}) {
  const start = new Date(s.startAt);
  const resolvedColor = s.color || s.eventColor || (s.id != null ? colorOverrides[String(s.id)] : null);
  return {
    ...s,
    color: normalizeScheduleColor(resolvedColor),
    date: toDateStr(start),
    time: `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}`,
  };
}

export default function TalentSchedulePage() {
  const [schedules, setSchedules] = useState([]);
  const [upcomingSchedules, setUpcomingSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [addInitialDate, setAddInitialDate] = useState('');
  const [editEvent, setEditEvent] = useState(null);
  const [detailEvent, setDetailEvent] = useState(null);
  const [dayModal, setDayModal] = useState(null);
  const colorOverridesRef = useRef({});

  const applyLocalColorFallback = useCallback((list, fallbackPayload) => {
    if (!fallbackPayload) return list;
    const fallbackKey = `${fallbackPayload.title}__${fallbackPayload.startAt}__${fallbackPayload.endAt || ''}__${fallbackPayload.location || ''}`;
    let matched = false;

    return list.map((item) => {
      if (matched || item.color || item.eventColor) return item;
      const itemKey = `${item.title}__${item.startAt}__${item.endAt || ''}__${item.location || ''}`;
      if (itemKey !== fallbackKey) return item;
      matched = true;
      return { ...item, color: normalizeScheduleColor(fallbackPayload.color) };
    });
  }, []);

  const fetchSchedules = useCallback(async (year, month, fallbackPayload = null) => {
    setIsLoading(true);
    try {
      const { startDate, endDate } = monthRange(year, month);
      const data = await getSchedules(startDate, endDate);
      const mapped = data.map((item) => mapSchedule(item, colorOverridesRef.current));
      setSchedules(applyLocalColorFallback(mapped, fallbackPayload));
    } catch {
      // silent — calendar stays empty on error
    } finally {
      setIsLoading(false);
    }
  }, [applyLocalColorFallback]);

  const fetchUpcomingSchedules = useCallback(async () => {
    try {
      const today = new Date();
      const end = new Date(today);
      end.setDate(end.getDate() + 7);
      const data = await getSchedules(toDateStr(today), toDateStr(end));
      const mapped = data.map((item) => mapSchedule(item, colorOverridesRef.current));
      setUpcomingSchedules(mapped);
    } catch {
      setUpcomingSchedules([]);
    }
  }, []);

  const handleMonthChange = useCallback((year, month) => {
    fetchSchedules(year, month);
  }, [fetchSchedules]);

  const handleAddEvent = async (payload) => {
    try {
      const created = await createSchedule(payload);
      if (created?.id != null) {
        colorOverridesRef.current[String(created.id)] = payload.color;
      }
      const start = new Date(payload.startAt);
      await fetchSchedules(start.getFullYear(), start.getMonth(), payload);
      await fetchUpcomingSchedules();
      setAddModal(false);
      setAddInitialDate('');
    } catch (err) {
      alert('일정 추가에 실패했습니다. 다시 시도해 주세요.');
    }
  };

  const handleEditEvent = async (payload) => {
    try {
      if (editEvent?.id != null) {
        colorOverridesRef.current[String(editEvent.id)] = payload.color;
      }
      await updateSchedule(editEvent.id, payload);
      const start = new Date(payload.startAt);
      await fetchSchedules(start.getFullYear(), start.getMonth());
      await fetchUpcomingSchedules();
      setEditEvent(null);
    } catch {
      alert('일정 수정에 실패했습니다. 다시 시도해 주세요.');
    }
  };

  const handleDeleteEvent = async (id) => {
    try {
      await deleteSchedule(id);
      setSchedules(prev => prev.filter(s => s.id !== id));
      setUpcomingSchedules(prev => prev.filter(s => s.id !== id));
    } catch {
      // TODO: show error toast
    }
    setDetailEvent(null);
    if (dayModal) {
      const remaining = dayModal.events.filter(e => e.id !== id);
      if (remaining.length === 0) setDayModal(null);
      else setDayModal(prev => ({ ...prev, events: remaining }));
    }
  };

  const handleDayClick = (date, dayEvents) => {
    if (dayEvents.length === 0) {
      setAddInitialDate(toDateStr(date));
      setAddModal(true);
    } else {
      setDayModal({ date, events: dayEvents });
    }
  };

  useEffect(() => {
    fetchUpcomingSchedules();
  }, [fetchUpcomingSchedules]);

  return (
    <div style={{ flex: 1, minWidth: 0, background: '#fcfcfd' }}>
      <div style={{ maxWidth: '1300px', margin: '0 auto', width: '100%' }}>
        <div style={{ padding: '50px 40px 40px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <h1 style={{ ...t('36px', 600, C.bk), margin: 0 }}>일정 관리</h1>
              <span style={t('16px', 400, C.sub)}>오디션, 미팅, 촬영 일정을 한눈에 확인하고 관리하세요.</span>
            </div>
            <button
              onClick={() => { setAddInitialDate(''); setAddModal(true); }}
              className={styles.addBtn}
            >
              <span style={{ fontSize: '18px', lineHeight: 1, color: C.bk }}>+</span>
              <span style={t('16px', 500, C.bk)}>새 일정 추가</span>
            </button>
          </div>
        </div>

        <div style={{ padding: '0 40px 60px' }}>
          <div style={{ display: 'flex', gap: '40px', alignItems: 'flex-start' }}>
            <CalendarGrid
              events={schedules}
              isLoading={isLoading}
              onEventClick={(ev) => setDetailEvent(ev)}
              onDayClick={handleDayClick}
              onMonthChange={handleMonthChange}
            />
            <UpcomingPanel events={upcomingSchedules} onEventClick={(ev) => setDetailEvent(ev)} maxItems={7} />
          </div>
        </div>
      </div>

      {addModal && (
        <AddEventModal
          initialDate={addInitialDate}
          onClose={() => { setAddModal(false); setAddInitialDate(''); }}
          onSave={handleAddEvent}
        />
      )}
      {editEvent && (
        <AddEventModal
          initialData={editEvent}
          onClose={() => setEditEvent(null)}
          onSave={handleEditEvent}
        />
      )}
      {detailEvent && (
        <EventDetailModal
          event={detailEvent}
          onClose={() => setDetailEvent(null)}
          onDelete={() => handleDeleteEvent(detailEvent.id)}
          onEdit={() => { setEditEvent(detailEvent); setDetailEvent(null); }}
        />
      )}
      {dayModal && (
        <DayEventsModal
          date={dayModal.date}
          events={dayModal.events}
          onClose={() => setDayModal(null)}
          onDelete={(id) => handleDeleteEvent(id)}
          onEdit={(ev) => { setEditEvent(ev); setDayModal(null); }}
        />
      )}
    </div>
  );
}
